import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { enqueueSnackbar } from 'notistack';
import AttachmentService from './attachment.service';
import dayjs from 'dayjs';

type ActionType = 'updateTicketAttachments';

interface MutationAction {
  actionType: ActionType;
  [key: string]: any;
}

const useTicketMutationsAttachments = (): {
  updateTicketAttachments: UseMutationResult<any, unknown, MutationAction, unknown>['mutate'];
  isUpdatingTicketAttachments: Record<string, boolean>;
  setIsUpdatingTicketAttachments: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  updateTicketAttachmentsMutation: UseMutationResult<any, unknown, MutationAction, unknown>;
} => {
  const [isUpdatingTicketAttachments, setIsUpdatingTicketAttachments] = useState<Record<string, boolean>>({});
  const queryClient = useQueryClient();
  const executeMutation = async ({ actionType, ...payload }: MutationAction) => {
    const attachmentService = AttachmentService();
    const action: { [key in ActionType]?: Function } = {
      updateTicketAttachments: attachmentService.postAttachments,
      // addAttachment: ticketService.postNewAttachment
    };

    const serviceMethod = action[actionType];
    if (!serviceMethod) {
      throw new Error(`Unknown action type: ${actionType}`);
    }
    return serviceMethod(payload);
  };

  const mutateAsync = useMutation<any, unknown, MutationAction>({
    mutationFn: executeMutation,
    onMutate: ({ actionType, ...newAttachment }) => {
      setIsUpdatingTicketAttachments({ ...isUpdatingTicketAttachments, [actionType]: true });

      const formData = newAttachment.data;
      const extractedData: Record<string, any> = {};

      for (const [key, value] of formData.entries()) {
        extractedData[key] = value;
      }
      const noteMetadata = JSON.parse(extractedData.noteMetadata || "{}");

      const preparedData = {
        AttachmentTypeId: noteMetadata.attachmenttypeid,
        UpdatedByName: noteMetadata.username,
        Fid: 0,
        UpdatedAt: dayjs(),
        Filename: extractedData.attachment.name,
      }

      queryClient.setQueryData(['attachments-by-id', noteMetadata.ticketid], (old: any) => {
        if (old) return [...old, preparedData]
        else return [preparedData]
      })
    },
    onSuccess: (data: any, variables: any) => {
      // console.log('Attachments onSuccess:', data, variables);
      // console.log('Mutation successful');
      // console.log('Attachment variables.data:', variables.data);
      // console.log('data.data.Success:', data.data.Success);
      // queryClient.invalidateQueries({ queryKey: ['all-tickets'] });
      if (typeof (data?.data?.Nid) === 'number' && variables.actionType === 'updateTicketAttachments') {
        // console.log('successful update');
        enqueueSnackbar('Attachment saved successfully', { variant: 'success' });
      } else if (typeof (data?.data?.Nid) !== 'number' && data.data.Success === false) {
        enqueueSnackbar('Attachment update failed', { variant: 'error' });
      }
      // if (variables.actionType === 'deleteTicket' && data.ticketId) {
      //     queryClient.removeQueries({ queryKey: ['ticket', data.ticketId] })
      //     enqueueSnackbar(`Ticket #${data.ticketId} deleted successfully`, { variant: 'success' })
      //     queryClient.invalidateQueries({ queryKey: ['archived-tickets'], exact: true });
      // }
    },
    onError: (error: any, variables: any) => {
      console.error('Error during mutation:', error);
      // console.warn('ERROR', variables);
      if (variables.actionType === 'updateTicketAttachments') {
        enqueueSnackbar('Attachment save failed', { variant: 'error' });
      }

      if (variables.actionType === 'deleteTicket' && variables.data.TId) {
        enqueueSnackbar(`Ticket #${variables.data[0].TicketID} delete failed`, {
          variant: 'error',
        });
      }
    },
    // onSettled: ({ actionType }) => {
    //   setIsUpdating({ ...isUpdating, [actionType]: false });
    // },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['attachments-by-id'] });
      queryClient.invalidateQueries({ queryKey: ['attachment-types-by-customer'] });

    },
  });

  return {
    updateTicketAttachments: mutateAsync.mutate,
    updateTicketAttachmentsMutation: mutateAsync,
    isUpdatingTicketAttachments,
    setIsUpdatingTicketAttachments
  };
};

export default useTicketMutationsAttachments;
